import { addHours } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { env } from '../../env';
import {
  GlobalActions,
  SignatureDocument,
  SignatureDocumentModule,
  ApiRequestStatus,
} from '../../model';
import appRoutes from '../../routing/app-routes';
import {
  messagesForSignatureAreaError,
  retrieveBundleUrlForSignature,
  selectSignRoomState,
  signatureAreaActions,
  SignatureAreaError,
  SignatureAreaSections,
  SignatureAreaState,
  signRoomSliceActions,
  SignRoomState,
  retrieveOnboardingSelfPracticeData,
  RootState,
  UserState,
  abortChangeContact,
  clearPendingContacts,
  updateUsername,
} from '../../store';
import {
  getDocumentsListBySection,
  getSelectedDocument,
  getSelectedModule,
  selectSignatureAreaState,
} from '../../store';
import {
  loadBundleDocumentDetails,
  loadOrdineRto,
  registerDisposition,
  rejectProposal as rejectProposalAction,
  retrieveBundleStatus,
  signatureArea,
} from '../../store/features/profile/signature-area/signature-area-thunk';
import { BUNDLE_ID_KEY, SELECTED_DOCUMENT_CLONE_KEY } from '../../utils';
import routeSectionMap from '../home/utils/route-section-map';
import { useAppDispatch, useAppSelector } from '../redux';
import { commonErrorMessages, signatureAreaMessages } from '../../messages';
import { useAppNavigation } from '../../utils/navigation.utils';
import { PraticaSelf } from '../../api/generated/dob';

export interface SignatureAreaLogic {
  documents: SignatureDocument[];
  selectedSection: SignatureAreaSections;
  selectedDocument: SignatureDocument | null;
  selectedModule: SignatureDocumentModule;
  isLoadingDocuments: boolean;
  isLoadingRejectProposal: boolean;
  isLoadingGetUrlForSignature: boolean;
  openSignedSucceededPopup: boolean;
  openSignedFailedPopup: boolean;
  setSelectedSection: (selectedSection: SignatureAreaSections) => void;

  setSelectedDocument: (documentId?: string) => void;
  setSelectedModule: (moduleId: string, documentId?: string) => void;
  startProcedure: (bundleId: string) => void;
  rejectProposal: (bundleId: string) => void;
  updateDocumentsList: (forceReloadPracticeData?: boolean) => void;
  handleCancelPractice: () => void;
  goToSignatureAreaFromProfile: () => void;

  goToSignatureDocument: () => void;
  backFromNamirial: () => Promise<void>;
  closeSignedNotificationPopup: () => void;
  practiceUnderProcessing: number;
  cancelPracticeButtonEnabled: boolean;
  isProspect: boolean;

  flagProfilaturaMifid: boolean;
  practiceData: PraticaSelf;
  signatureAreaRequestStatus: ApiRequestStatus;
  fetchOrdineRto?: (bundleId: string, practiceNumber: string) => Promise<any>;
  loadBundleDetails?: (bundleId: string) => void;
}

export const useSignatureAreaLogic = (
  actions: GlobalActions
): SignatureAreaLogic => {
  const { showToast, closeToast, getStorage } = actions;
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { appNavigate } = useAppNavigation();
  const [bundleIdStored, setBundleIdStored] = useState<string>();

  const {
    signatureAreaRequestStatus,
    signatureAreaError,
    selectedSection,
    rejectProposalError,
    rejectProposalRequestStatus,
    bundleStatus,
    bundleStatusRequestStatus,
    bundleStatusError,
    selectedDocumentClone,
  } = useAppSelector<SignatureAreaState>(selectSignatureAreaState);

  const {
    bundleUrlToSignError,
    bundleUrlToSignRequestStatus,
    bundleUrlToSign,
  } = useAppSelector<SignRoomState>(selectSignRoomState);

  const { signFilesPhoneNumber, signFilesEmail } = useAppSelector(
    (state) => state.data.user
  );

  const userState = useAppSelector((state) => state.data.user);

  const [cancelPracticeButtonEnabled, enableCancelPracticeButton] =
    useState(false);

  useEffect(() => {
    dispatch(retrieveOnboardingSelfPracticeData());
  }, [dispatch]);

  const { practiceData } = useAppSelector(
    (state: RootState) => state.features.home
  );

  useEffect(() => {
    if (practiceData.requestStatus === 'succeeded') {
      enableCancelPracticeButton(
        practiceData?.value?.statoPratica &&
          ['Bozza', 'Da Firmare'].includes(practiceData?.value?.statoPratica)
      );
    } else {
      enableCancelPracticeButton(false);
    }
  }, [practiceData.requestStatus]);

  const documents: any = useAppSelector(getDocumentsListBySection);

  const selectedDocument: any = useAppSelector(getSelectedDocument);
  const selectedModule: any = useAppSelector(getSelectedModule);

  const {
    token,
    taxCode,
    bundleId: documentToSignId,
  }: UserState = useAppSelector((state) => state.data?.user || {});

  const { isProspect } = useAppSelector<UserState>((state) => state.data.user);
  const setSelectedSection = useCallback(
    (section: SignatureAreaSections) => {
      dispatch(signatureAreaActions.updateSelectedSection(section));
    },
    [dispatch]
  );

  const loadBundleDetails = useCallback(
    (bundleId: string) => {
      if (bundleId && !isProspect) {
        dispatch(loadBundleDocumentDetails(bundleId));
      }
    },
    [dispatch, isProspect]
  );

  const setSelectedDocument = useCallback(
    (value: string) => {
      dispatch(signatureAreaActions.setSelectedDocumentState(value));
      if (value) {
        const doc = (documents as SignatureDocument[])?.find((d) => d.id === value);
        if (doc && (!doc.modules || doc.modules.length === 0) && !doc.isModulesLoading && !isProspect) {
          dispatch(loadBundleDocumentDetails(value));
        }
      }
    },
    [dispatch, documents, isProspect]
  );

  const fetchOrdineRto = useCallback(
    async (bundleId: string, practiceNumber: string) => {
      return await dispatch(loadOrdineRto({ bundleId, practiceNumber })).unwrap();
    },
    [dispatch]
  );

  const setSelectedModule = useCallback(
    (moduleId: string, documentId?: string) => {
      if (documentId) {
        dispatch(signatureAreaActions.setSelectedDocumentState(documentId));
      }
      dispatch(signatureAreaActions.setSelectedModuleIdState(moduleId));
    },
    [dispatch]
  );

  const startProcedure = useCallback(
    (bundleId: string) => {
      dispatch(retrieveBundleUrlForSignature({ bundleId }));
      setBundleIdStored(bundleId);
    },
    [dispatch, setBundleIdStored]
  );

  const rejectProposal = useCallback(
    (bundleId: string) => {
      dispatch(rejectProposalAction(bundleId));
    },
    [dispatch]
  );

  const showToastError = useCallback(
    (error: SignatureAreaError) => {
      const errorMessages = messagesForSignatureAreaError(error);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
    },
    [closeToast, formatMessage, showToast]
  );

  useEffect(() => {
    if (signatureAreaError) {
      showToastError(signatureAreaError);
      dispatch(signatureAreaActions.clearSignatureAreaError());
    }
  }, [dispatch, showToastError, signatureAreaError]);

  const updateDocumentsList = useCallback(
    (forceReloadPracticeData = false) => {
      if (token) {
        if (
          taxCode &&
          isProspect &&
          (!practiceData.value || forceReloadPracticeData)
        ) {
          dispatch(retrieveOnboardingSelfPracticeData());
        }

        dispatch(
          signatureArea({
            customerCode: practiceData.value?.prospectCode,
            bundleCode: practiceData.value?.bundleCode,
          })
        );
      }
    },
    [
      token,
      isProspect,
      taxCode,
      practiceData.value?.bundleCode,
      practiceData.value?.prospectCode,
      dispatch,
    ]
  );

  const handleCancelPractice = useCallback(() => {
    showToast({
      type: 'warning',
      title: formatMessage(signatureAreaMessages.cancelPracticeToastTitle),
      description: formatMessage(
        signatureAreaMessages.cancelPracticeToastDescription
      ),
      close: closeToast,
    });
  }, [closeToast, formatMessage, showToast]);

  const goToSignFiles = useCallback(() => {
    if (documents.length > 0) {
      setSelectedDocument(documentToSignId);
      dispatch(signatureAreaActions.updateSelectedSection('ALL'));
    }
  }, [dispatch, documents, setSelectedDocument, documentToSignId]);

  const goToSignatureDocument = useCallback(() => {
    goToSignFiles();
    appNavigate(appRoutes.Sign, { state: { openDocumentDetail: true } });
  }, [goToSignFiles, appNavigate]);

  const goToSignatureAreaFromProfile = useCallback(() => {
    appNavigate(routeSectionMap.routeForSection('sign'));
  }, [goToSignFiles, appNavigate]);

  useEffect(() => {
    if (rejectProposalError) {
      showToastError(rejectProposalError);
      dispatch(signatureAreaActions.clearRejectProposalError());
    }
  }, [showToastError, dispatch, rejectProposalError]);

  useEffect(() => {
    if (bundleUrlToSignError) {
      showToastError(bundleUrlToSignError);
      dispatch(signRoomSliceActions.clearBundleUrlToSignError());
      setBundleIdStored(undefined);
    }
  }, [showToastError, dispatch, setBundleIdStored, bundleUrlToSignError]);

  useEffect(() => {
    if (bundleUrlToSignRequestStatus === 'succeeded') {
      dispatch(signRoomSliceActions.clearBundleUrlToSignError());
      if (bundleUrlToSign) {
        getStorage().storeValue(
          BUNDLE_ID_KEY,
          bundleIdStored,
          addHours(new Date(), 1)
        );

        if (selectedDocumentClone) {
          getStorage().storeValue(
            SELECTED_DOCUMENT_CLONE_KEY,
            JSON.stringify(selectedDocumentClone),
            addHours(new Date(), 1)
          );
        }

        if (env.appModule === 'web') {
          window.location.href = bundleUrlToSign;
        } else {
          appNavigate(appRoutes.SignRoomPage, {
            state: { from: appRoutes.Sign },
          });
        }
      } else {
        showToast({
          type: 'error',
          title: formatMessage(commonErrorMessages.errorTitlePopup),
          description: formatMessage(
            commonErrorMessages.errorDescriptionDefault
          ),
          close: closeToast,
        });
      }
    }
  }, [
    bundleUrlToSignRequestStatus,
    bundleUrlToSign,
    bundleIdStored,
    getStorage,
    appNavigate,
    dispatch,
    selectedDocumentClone,
  ]);

  useEffect(() => {
    if (rejectProposalRequestStatus === 'succeeded') {
      dispatch(signatureAreaActions.clearRejectProposalError());
      updateDocumentsList();
      if (env.appModule === 'mobile') {
        appNavigate(routeSectionMap.routeForSection('sign'), { replace: true });
        setTimeout(() => {
          dispatch(signatureAreaActions.clearSelectedDocuments());
        }, 100);
      }
    }
  }, [rejectProposalRequestStatus, updateDocumentsList, appNavigate, dispatch]);

  useEffect(() => {
    if (bundleStatusError) {
      showToastError(bundleStatusError);
      dispatch(signatureAreaActions.clearBundleStatus());
    }
  }, [bundleStatusError, dispatch, showToastError]);

  const backFromNamirial = useCallback(async () => {
    const bundleId = await getStorage().readValue(BUNDLE_ID_KEY);
    if (await bundleId) {
      await getStorage().deleteValue(BUNDLE_ID_KEY);

      const savedDocumentClone = await getStorage().readValue(
        SELECTED_DOCUMENT_CLONE_KEY
      );
      if (savedDocumentClone) {
        try {
          const parsedDoc = JSON.parse(savedDocumentClone);
          dispatch(signatureAreaActions.setSelectedDocumentClone(parsedDoc));
          await getStorage().deleteValue(SELECTED_DOCUMENT_CLONE_KEY);
        } catch (e) {
          console.error('Error parsing saved document clone', e);
        }
      }

      dispatch(retrieveBundleStatus(bundleId));
      dispatch(registerDisposition(bundleId));
      appNavigate(appRoutes.Sign);
    }
  }, [dispatch, getStorage, appNavigate]);

  const closeSignedNotificationPopup = useCallback(() => {
    if (
      selectedDocumentClone &&
      selectedDocumentClone?.type?.practiceType === 'ContactData' &&
      bundleStatus === 'SIGNED'
    ) {
      const { pendingNewEmail } = userState;
      if (pendingNewEmail && userState.username === userState.email) {
        dispatch(updateUsername(pendingNewEmail));
      }

      dispatch(clearPendingContacts());
      dispatch(signatureAreaActions.clearSelectedDocumentClone());
    }
    updateDocumentsList();
    dispatch(signatureAreaActions.clearBundleStatus());
  }, [
    dispatch,
    updateDocumentsList,
    selectedDocumentClone,
    bundleStatus,
    userState,
  ]);

  useEffect(() => {
    if (selectedDocument && !selectedDocumentClone) {
      dispatch(
        signatureAreaActions.setSelectedDocumentClone(
          structuredClone(selectedDocument)
        )
      );
    }
  }, [selectedDocument, selectedDocumentClone, dispatch]);

  const practiceUnderProcessing = useMemo(() => {
    if (documents) {
      const d = documents as SignatureDocument[];
      return d?.filter((doc) => doc.underProcessing)?.length || 0;
    }
    return 0;
  }, [documents]);

  useEffect(() => {
    if (documents == null) {
      return;
    }

    const signEmail = signFilesEmail ?? false;
    const signPhone = signFilesPhoneNumber ?? false;
    if (!signEmail && !signPhone) {
      return;
    }

    const contactDataExpired = documents.filter(
      (d) => d.type?.practiceType === 'ContactData' && d.isExpired
    );

    if (contactDataExpired.length === 0) {
      return;
    }

    dispatch(
      abortChangeContact({
        contactType: signFilesPhoneNumber ? 'PHONE' : 'EMAIL',
      })
    );
  }, [dispatch, documents, signFilesEmail, signFilesPhoneNumber]);

  const signSucceeded = useMemo(
    () => ['SIGNED', 'SIGNING'].includes(bundleStatus || ''),
    [bundleStatus]
  );

  return {
    documents,
    selectedSection,
    selectedDocument,
    selectedModule,
    isLoadingDocuments:
      signatureAreaRequestStatus === 'loading' ||
      bundleStatusRequestStatus === 'loading',
    setSelectedSection,
    setSelectedDocument,
    setSelectedModule,
    startProcedure,
    rejectProposal,
    updateDocumentsList,
    goToSignatureAreaFromProfile,
    goToSignatureDocument,
    backFromNamirial,
    closeSignedNotificationPopup,
    isLoadingRejectProposal: rejectProposalRequestStatus === 'loading',
    isLoadingGetUrlForSignature: bundleUrlToSignRequestStatus === 'loading',
    openSignedSucceededPopup:
      bundleStatusRequestStatus === 'succeeded' && signSucceeded,
    openSignedFailedPopup:
      bundleStatusRequestStatus === 'succeeded' && !signSucceeded,
    practiceUnderProcessing,
    handleCancelPractice,
    cancelPracticeButtonEnabled,
    isProspect,
    flagProfilaturaMifid: practiceData?.value?.flagProfilaturaMifid,
    practiceData: practiceData.value,
    signatureAreaRequestStatus,
    fetchOrdineRto,
    loadBundleDetails,
  };
};
